import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Loader2, FileText, ArrowRight, Plus, Crown, AlertCircle } from "lucide-react";

const STATUS_LABEL = { nuova: "Nuova", presa_in_carico: "Presa in carico", in_lavorazione: "In lavorazione", chiusa: "Chiusa" };

const STATUS_BADGE = {
  trial: { txt: "Prova 14gg", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  active: { txt: "Attivo", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  admin_gift: { txt: "Regalo", cls: "bg-violet-100 text-violet-800 border-violet-200" },
};

function PlanBadge() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/consultant/me/subscription").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return null;

  if (!data.has_active_plan) {
    return (
      <div className="mb-6 rounded-2xl border border-rose-200 bg-rose-50 p-4 flex items-start gap-3" data-testid="no-plan-banner">
        <AlertCircle className="w-5 h-5 text-rose-600 mt-0.5 shrink-0" />
        <div className="text-sm">
          <div className="font-black text-rose-900">Nessun piano attivo</div>
          <div className="text-rose-700 mt-0.5">
            Contatta l&apos;amministratore per attivare un piano. Le nuove pratiche sono temporaneamente bloccate.
          </div>
        </div>
      </div>
    );
  }

  const sub = data.subscription;
  const plan = data.plan;
  const usage = data.usage;
  const badge = STATUS_BADGE[sub.status] || { txt: sub.status, cls: "bg-slate-100" };
  const endDate = new Date(sub.current_period_end).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });

  const practicesPct = usage.max_practices_per_month
    ? Math.min(100, Math.round(100 * usage.practices_this_month / usage.max_practices_per_month))
    : 0;
  const storagePct = usage.storage_pct ?? 0;

  return (
    <div className="mb-6 rounded-2xl border border-slate-200 bg-gradient-to-r from-white to-slate-50 p-4 sm:p-5" data-testid="plan-badge">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#F77F2A]/10 flex items-center justify-center shrink-0">
            <Crown className="w-5 h-5 text-[#F77F2A]" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-black text-[#1E293B]">Piano {plan.name}</div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-black border ${badge.cls}`}>{badge.txt}</span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">Scadenza: {endDate}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
        <div className="text-xs">
          <div className="flex justify-between mb-1">
            <span className="text-slate-500">Pratiche questo mese</span>
            <span className="font-bold text-[#1E293B]">
              {usage.practices_this_month}
              {usage.max_practices_per_month !== null ? ` / ${usage.max_practices_per_month}` : " · illimitate"}
            </span>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-[#F77F2A] transition-all" style={{ width: `${practicesPct}%` }} />
          </div>
        </div>
        <div className="text-xs">
          <div className="flex justify-between mb-1">
            <span className="text-slate-500">Archivio media</span>
            <span className="font-bold text-[#1E293B]">
              {usage.storage_mb < 1024 ? `${usage.storage_mb} MB` : `${(usage.storage_mb / 1024).toFixed(2)} GB`}
              {usage.storage_limit_gb ? ` / ${usage.storage_limit_gb} GB` : ""}
            </span>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-[#2456C6] transition-all" style={{ width: `${storagePct}%` }} />
          </div>
        </div>
      </div>
    </div>
  );
}

const STATUS_LABEL_DUMMY = STATUS_LABEL; // eslint-disable-line no-unused-vars

function PracticeRow({ p }) {
  const dt = new Date(p.created_at);
  return (
    <Link to={`/dashboard/practice/${p.id}`} data-testid={`practice-row-${p.code}`}
      className="grid grid-cols-12 items-center gap-3 py-4 px-4 border-b border-slate-100 hover:bg-slate-50 transition-colors">
      <div className="col-span-12 sm:col-span-2 font-black text-[#1E293B]">{p.code}</div>
      <div className="col-span-12 sm:col-span-2 text-sm text-slate-600">{dt.toLocaleDateString("it-IT")} <span className="text-slate-400 ml-1">{dt.toLocaleTimeString("it-IT", {hour:'2-digit',minute:'2-digit'})}</span></div>
      <div className="col-span-12 sm:col-span-3 text-sm">
        <div className="font-bold text-[#1E293B]">{p.client_first_name} {p.client_last_name}</div>
        <div className="text-xs text-slate-500">{p.vehicle_plate}</div>
      </div>
      <div className="col-span-12 sm:col-span-2 text-sm text-slate-600">
        <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `tel:${p.client_phone}`; }}
          className="hover:text-[#2456C6] cursor-pointer">
          {p.client_phone}
        </span>
      </div>
      <div className="col-span-10 sm:col-span-2"><span className={`badge-status badge-${p.status}`}>{STATUS_LABEL[p.status]}</span></div>
      <div className="col-span-2 sm:col-span-1 flex justify-end"><ArrowRight className="w-4 h-4 text-slate-400" /></div>
    </Link>
  );
}

export default function ConsultantDashboard() {
  const [practices, setPractices] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/practices");
      setPractices(data);
    } catch {
      setPractices([]);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = practices?.filter((p) => {
    if (filter !== "all" && p.status !== filter) return false;
    if (search) {
      const s = search.toLowerCase();
      return p.code.toLowerCase().includes(s)
        || `${p.client_first_name} ${p.client_last_name}`.toLowerCase().includes(s)
        || p.vehicle_plate.toLowerCase().includes(s)
        || p.client_phone.includes(s);
    }
    return true;
  });

  const counts = practices ? {
    all: practices.length,
    nuova: practices.filter(p => p.status === "nuova").length,
    presa_in_carico: practices.filter(p => p.status === "presa_in_carico").length,
    in_lavorazione: practices.filter(p => p.status === "in_lavorazione").length,
    chiusa: practices.filter(p => p.status === "chiusa").length,
  } : { all: 0, nuova: 0, presa_in_carico: 0, in_lavorazione: 0, chiusa: 0 };

  return (
    <DashboardLayout role="consultant">
      <div className="p-4 sm:p-8">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-[#1E293B]">Pratiche</h1>
            <p className="text-sm text-slate-500">Gestisci le segnalazioni dei tuoi clienti</p>
          </div>
          <Link to="/dashboard/new" data-testid="new-practice-button" className="btn-gs-primary !py-3">
            <Plus className="w-5 h-5" /> Nuova pratica
          </Link>
        </div>

        <PlanBadge />

        {/* Stats cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {[
            { k: "all", label: "Totali", color: "#1E293B" },
            { k: "nuova", label: "Nuove", color: "#3B82F6" },
            { k: "presa_in_carico", label: "Prese in carico", color: "#F77F2A" },
            { k: "in_lavorazione", label: "In lavorazione", color: "#EAB308" },
            { k: "chiusa", label: "Chiuse", color: "#22C55E" },
          ].map((s) => (
            <button key={s.k} onClick={() => setFilter(s.k)} data-testid={`stat-${s.k}`}
              className={`text-left p-4 rounded-2xl border-2 transition-all ${
                filter === s.k ? "border-[#1E293B] bg-white" : "border-transparent bg-white hover:border-slate-200"
              }`}>
              <div className="text-2xl font-black" style={{ color: s.color }}>{counts[s.k]}</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider font-bold mt-1">{s.label}</div>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="card-gs !p-3 mb-4">
          <input data-testid="search-input" type="text" placeholder="Cerca per codice, nome, targa, telefono…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full px-4 py-2 bg-transparent border-0 focus:outline-none text-sm" />
        </div>

        {/* List */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="grid grid-cols-12 gap-3 py-3 px-4 border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider font-bold text-slate-500">
            <div className="col-span-12 sm:col-span-2">Codice</div>
            <div className="hidden sm:block col-span-2">Data</div>
            <div className="hidden sm:block col-span-3">Cliente</div>
            <div className="hidden sm:block col-span-2">Telefono</div>
            <div className="hidden sm:block col-span-2">Stato</div>
            <div className="hidden sm:block col-span-1"></div>
          </div>

          {practices === null ? (
            <div className="py-20 text-center"><Loader2 className="w-8 h-8 animate-spin text-[#2456C6] mx-auto" /></div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center text-slate-500">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <div className="font-bold text-[#1E293B] mb-1">Nessuna pratica</div>
              <p className="text-sm">Condividi il tuo QR Code per ricevere segnalazioni.</p>
            </div>
          ) : (
            filtered.map((p) => <PracticeRow key={p.id} p={p} />)
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
